import { ORDER_PAYMENT_STATES, PaymentGateway } from '../services/exports/Constants';

/**
 * Non-Spreedly gateways keep the schedule the order screen always had: polls at 3 s, 5 s and
 * 10 s after mount — expressed here as the gaps between consecutive polls.
 */
const DEFAULT_DELAYS_MS = [3000, 2000, 5000];

/**
 * A Spreedly 3DS settle can trail the customer's return by a while (backend confirm retries, then
 * the reconcile sweep), so back off instead of giving up at 10 s — bounded so a truly abandoned
 * order does not poll forever.
 */
const SPREEDLY_DELAYS_MS = [2000, 3000, 5000, 8000, 13000, 20000, 30000];
const SPREEDLY_BUDGET_MS = 180_000;

export interface PaymentStatusPollArgs {
  gateway: PaymentGateway | null | undefined;
  /** Polls already made, the initial one included — the first call after mount passes 1. */
  attempt: number;
  /** Milliseconds since polling started. */
  elapsedMs: number;
  /** The derived `payment_state` the last poll returned, if it returned one. */
  paymentState?: ORDER_PAYMENT_STATES | null;
}

/**
 * Delay before the next payment-status poll, or `null` to stop.
 *
 * Stops on any terminal `payment_state` — anything other than awaiting_payment means the backend
 * has decided and the order screen already reacts (paid loads the order, failed shows the
 * modal). A missing state (fetch error, older payload) keeps the schedule going.
 */
export default function nextPaymentStatusPollDelay(args: PaymentStatusPollArgs): number | null {
  const { gateway, attempt, elapsedMs, paymentState } = args;

  if (paymentState && paymentState !== ORDER_PAYMENT_STATES.AWAITING_PAYMENT) {
    return null;
  }

  const index = Math.max(0, attempt - 1);

  if (gateway !== PaymentGateway.Spreedly) {
    return DEFAULT_DELAYS_MS[index] ?? null;
  }

  if (elapsedMs >= SPREEDLY_BUDGET_MS) {
    return null;
  }

  return SPREEDLY_DELAYS_MS[Math.min(index, SPREEDLY_DELAYS_MS.length - 1)];
}
